import { useState } from 'react';
import { Button, Popover, Tabs } from 'antd';
import { SmileOutlined } from '@ant-design/icons';

const EMOJI_GROUPS = [
    { key: 'smileys', label: '😀', emojis: '😀 😃 😄 😁 😆 😅 😂 🤣 😊 😇 🙂 🙃 😉 😌 😍 🥰 😘 😗 😙 😚 😋 😛 😝 😜 🤪 🤨 🧐 🤓 😎 🥳 😏 😒 😞 😔 😟 😕 🙁 😣 😖 😫 😩 🥺 😢 😭 😤 😠 😡 🤯 😳 🥵 🥶 😱 😨 😰 😥 😓 🤗 🤔 🤭 🤫 🤥 😶 😐 😑 😬 🙄 😯 😦 😧 😮 😲 🥱 😴 🤤 😪 😵 🤐 🥴 🤢 🤮 🤧 😷 🤒 🤕' },
    { key: 'gestures', label: '👍', emojis: '👍 👎 👌 🤌 🤏 ✌️ 🤞 🤟 🤘 🤙 👈 👉 👆 👇 ☝️ ✋ 🤚 🖐️ 🖖 👋 👏 🙌 👐 🤲 🤝 🙏 ✍️ 💪 🫶 👀 🧠 🫡' },
    { key: 'hearts', label: '❤️', emojis: '❤️ 🧡 💛 💚 💙 💜 🖤 🤍 🤎 💔 ❣️ 💕 💞 💓 💗 💖 💘 💝 ✨ ⭐ 🌟 💫 🔥 💯 ✅ ❌ ❗ ❓ 💡 🎉 🎊 🎈 🎁 🏆 🥇' },
    { key: 'nature', label: '🌿', emojis: '🐶 🐱 🐭 🐰 🦊 🐻 🐼 🐨 🐯 🦁 🐮 🐷 🐸 🐵 🐔 🐧 🐦 🦋 🐝 🐢 🐙 🐬 🐳 🌸 🌺 🌻 🌼 🌷 🌱 🌿 🍀 🍁 🍂 🌳 🌲 🌵 🌊 🌈 ☀️ 🌙 ⛅ 🌧️ ❄️' },
    { key: 'food', label: '🍕', emojis: '🍏 🍎 🍐 🍊 🍋 🍌 🍉 🍇 🍓 🫐 🍒 🍑 🥭 🍍 🥥 🥑 🥦 🥕 🌽 🍞 🧀 🥚 🍳 🥞 🍔 🍟 🍕 🌮 🍣 🍜 🍰 🎂 🍪 🍫 🍩 ☕ 🍵 🍺 🍷 🥂' },
    { key: 'activities', label: '⚽', emojis: '⚽ 🏀 🏈 ⚾ 🎾 🏐 🏓 🏸 ⛳ 🚴 🏃 🧘 🏊 🎨 🎭 🎤 🎧 🎸 🎹 🎲 🎮 📚 ✏️ 📅 📌 📎 💻 📱 📷 🚗 ✈️ 🚀 🏠 🏖️ ⛰️' },
];

/**
 * Smiley button that opens a small emoji grid; onSelect receives the emoji
 * string. By default it stays open after a pick so several emojis can be
 * typed in a row — reactions pass closeOnSelect. `trigger` swaps the default
 * button for a custom element.
 */
export default function EmojiPicker({ onSelect, disabled = false, size, closeOnSelect = false, trigger }) {
    const [open, setOpen] = useState(false);

    const content = (
        <Tabs
            size="small"
            style={{ width: 288 }}
            items={EMOJI_GROUPS.map(group => ({
                key: group.key,
                label: <span style={{ fontSize: 16 }}>{group.label}</span>,
                children: (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', maxHeight: 200, overflowY: 'auto' }}>
                        {group.emojis.split(' ').map(emoji => (
                            <Button
                                key={emoji}
                                type="text"
                                onClick={() => { onSelect(emoji); if (closeOnSelect) setOpen(false); }}
                                style={{ fontSize: 20, padding: 0, height: 34 }}
                            >
                                {emoji}
                            </Button>
                        ))}
                    </div>
                ),
            }))}
        />
    );

    return (
        <Popover content={content} trigger="click" placement="topLeft" open={open} onOpenChange={setOpen}>
            {trigger ?? <Button icon={<SmileOutlined />} size={size} disabled={disabled} aria-label="Insert emoji" />}
        </Popover>
    );
}

/**
 * Insert text at an antd TextArea's caret (falls back to appending) and put
 * the caret back just after it, so picking an emoji mid-sentence works.
 */
export function insertAtCaret(textAreaRef, value, setValue, text) {
    const el = textAreaRef.current?.resizableTextArea?.textArea;
    const start = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? value.length;
    setValue(value.slice(0, start) + text + value.slice(end));
    if (el) {
        requestAnimationFrame(() => {
            el.focus();
            el.setSelectionRange(start + text.length, start + text.length);
        });
    }
}
